import React, { useState, useEffect } from 'react';
import { 
  useStripe, 
  useElements, 
  PaymentElement,
  Elements 
} from '@stripe/react-stripe-js';
import { useStripeContext } from '../../contexts/StripeContext';
import { paymentService } from '../../services/paymentService';
import Button from '../ui/Button';
import Icon from '../AppIcon';

const JobPaymentFormInner = ({
  clientSecret,
  amount,
  postingType,
  onSuccess,
  onError,
  onCancel
}) => {
  const stripe = useStripe();
  const elements = useElements();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [paymentReady, setPaymentReady] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);

  useEffect(() => {
    setErrorMessage('');
    setPaymentReady(false);
  }, [clientSecret]);

  useEffect(() => {
    if (elements) {
      const paymentElement = elements?.getElement(PaymentElement);
      if (paymentElement) {
        paymentElement?.on('ready', () => {
          setPaymentReady(true);
        });
      }
    }
  }, [elements]);

  const handleSubmit = async (event) => {
    event?.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      setErrorMessage('Payment system not ready. Please try again.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage('');

    try {
      const { error: stripeError, paymentIntent } = await stripe?.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location?.origin}/employer-dashboard`,
        },
        redirect: 'if_required'
      });

      if (stripeError) {
        setErrorMessage(stripeError?.message || 'Payment failed. Please try again.');
        onError?.(stripeError);
      } else if (paymentIntent) {
        if (paymentIntent?.status === 'succeeded') {
          const { data, error } = await paymentService?.confirmJobPayment(paymentIntent?.id);
          
          const successResult = {
            paymentIntent,
            paymentData: data,
            warning: error ? 'Payment processed but confirmation failed. Please contact support if needed.' : undefined
          };

          setShowSuccessAnimation(true);

          setTimeout(() => {
            setShowSuccessAnimation(false);
            onSuccess?.(successResult);
          }, 2000);
        } else if (paymentIntent?.status === 'requires_action') {
          setErrorMessage('Additional authentication required. Please complete the verification.');
        } else {
          setErrorMessage('Payment processing incomplete. Please try again.');
        }
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      setErrorMessage('An unexpected error occurred. Please try again.');
      onError?.(error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!stripe || !elements || !clientSecret) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-muted rounded w-1/4"></div>
          <div className="h-10 bg-muted rounded"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
          <div className="h-10 bg-muted rounded"></div>
        </div>
        <p className="text-sm text-muted-foreground mt-4 text-center">
          Loading payment system...
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-primary/5 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Total Amount</p>
              <p className="text-2xl font-bold text-foreground">
                {paymentService?.formatAmount(amount)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground mb-1">Posting Type</p>
              <p className="text-lg font-semibold text-primary capitalize">
                {postingType}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-card-foreground">
              Payment Details
            </label>
            <PaymentElement 
              options={{
                fields: {
                  billingDetails: 'auto'
                },
                layout: 'tabs'
              }}
            />
          </div>
        </div>

        {errorMessage && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Icon name="AlertCircle" size={18} color="var(--color-destructive)" />
              <p className="text-destructive text-sm">{errorMessage}</p>
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isProcessing}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!paymentReady || isProcessing || !stripe || !elements}
            loading={isProcessing}
            className="flex-1"
            iconName={isProcessing ? "Loader2" : "CreditCard"}
            iconClassName={isProcessing ? "animate-spin" : ""}
            iconPosition="left"
          >
            {isProcessing ? 'Processing...' : 'Complete Payment'}
          </Button>
        </div>

        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Icon name="Shield" size={14} />
          <span>Secured by Stripe • Your payment information is encrypted</span>
        </div>
      </form>
      {showSuccessAnimation && (
        <div className="absolute inset-0 bg-white/95 backdrop-blur-sm rounded-lg flex items-center justify-center z-50">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center animate-bounce">
              <Icon name="Check" size={32} color="#22c55e" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-green-600">Payment Successful!</h3>
              <p className="text-sm text-muted-foreground mt-1">Processing your job posting...</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const JobPaymentForm = (props) => {
  const { stripePromise, stripeOptions } = useStripeContext();

  if (!props?.clientSecret) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <p className="text-center text-muted-foreground">
          Preparing payment system...
        </p>
      </div>
    );
  }

  const elementsOptions = {
    clientSecret: props?.clientSecret,
    ...stripeOptions
  };

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <JobPaymentFormInner {...props} />
    </Elements>
  );
};

export default JobPaymentForm;